import { QueryServerKeys } from 'QueryServerKeys';
import { queryClient } from 'index';
import { getJobs, getSchemasAndTables, getStatusCounts } from 'services/jobs';
import { JobStatus } from 'components/StatusTag/StatusTag';
import { seachableTablePageSizeOptions } from 'types/types';
import dayjs from 'dayjs';

const prefetchQueryOptions = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
} as const;

export type JobsPaginatedQueryParams = {
  clusterName: string;
  pageNumber: number;
  pageSize: number;
  tableName?: string | string[];
  schemaName?: string | string[];
  status?: string | string[];
  sortBy?: string | null;
  sortOrder?: string | null;
  isSignedURL?: boolean;
  expires?: string;
  signature?: string;
  prefix?: string;
  end_date?: string;
  start_date?: string;
};

export function getJobsPaginatedQueryKey({
  tableName,
  schemaName,
  clusterName,
  pageNumber,
  pageSize,
  sortBy,
  sortOrder,
  start_date,
  end_date,
  status,
}: JobsPaginatedQueryParams) {
  return [
    !tableName && !schemaName
      ? QueryServerKeys.JOBS.GET_ALL_JOBS_PAGINATED
      : QueryServerKeys.JOBS.GET_TABLE_DETAILS_JOBS_PAGINATED,
    clusterName,
    pageNumber,
    pageSize,
    sortBy,
    sortOrder,
    tableName,
    schemaName,
    start_date,
    end_date,
    status,
  ];
}

export async function fetchJobsPaginatedPage({
  pageNumber,
  pageSize,
  isSignedURL,
  sortBy,
  sortOrder,
  tableName,
  schemaName,
  status,
  start_date,
  end_date,
  expires,
  signature,
  prefix,
  clusterName,
}: JobsPaginatedQueryParams) {
  const res = await getJobs({
    pageNumber,
    pageSize,
    isSignedURL,
    ...(sortBy ? { sortBy } : {}),
    ...(sortOrder ? { sortOrder } : {}),
    ...(tableName ? { tableName } : {}),
    ...(schemaName ? { schemaName } : {}),
    ...(status ? { status } : {}),
    ...(start_date ? { start_date } : {}),
    ...(end_date ? { end_date } : {}),
    ...(isSignedURL && expires ? { expires } : {}),
    ...(isSignedURL && signature ? { signature } : {}),
    ...(isSignedURL && prefix ? { prefix } : {}),
    ...(isSignedURL && clusterName ? { clusterName } : {}),
  });
  return res?.data;
}

export function buildJobsTablePaginatedParams({
  clusterName,
  pageNumber,
  pageSize,
  tableFilter,
  schemaFilters,
  jobStatusFilters,
  sortBy,
  sortOrder,
  dateRangePicker,
  isSignedURL,
  expires,
  signature,
  prefix,
  lockedTableFilter,
  lockedSchemaFilter,
}: {
  clusterName: string;
  pageNumber: number;
  pageSize: number;
  tableFilter: string[];
  schemaFilters: string[];
  jobStatusFilters: JobStatus[];
  sortBy: string | null;
  sortOrder: string | null;
  dateRangePicker: dayjs.Dayjs[] | null[];
  isSignedURL: boolean;
  expires?: string;
  signature?: string;
  prefix?: string;
  lockedTableFilter?: string[];
  lockedSchemaFilter?: string[];
}): JobsPaginatedQueryParams {
  return {
    clusterName,
    pageNumber,
    pageSize,
    isSignedURL,
    ...(isSignedURL && expires ? { expires } : {}),
    ...(isSignedURL && signature ? { signature } : {}),
    ...(isSignedURL && prefix ? { prefix } : {}),
    tableName:
      isSignedURL && lockedTableFilter ? lockedTableFilter[0] : tableFilter,
    schemaName:
      isSignedURL && lockedSchemaFilter ? lockedSchemaFilter[0] : schemaFilters,
    status: jobStatusFilters,
    sortBy,
    sortOrder,
    start_date: dateRangePicker?.[0]?.toISOString(),
    end_date: dateRangePicker?.[1]?.toISOString(),
  };
}

export function buildDefaultJobsFirstPageParams({
  clusterName,
  sortBy,
  sortOrder,
  pageSize = seachableTablePageSizeOptions[0],
}: {
  clusterName: string;
  sortBy?: string | null;
  sortOrder?: string | null;
  pageSize?: number;
}): JobsPaginatedQueryParams {
  return {
    clusterName,
    pageNumber: 1,
    pageSize,
    tableName: [],
    schemaName: [],
    status: [],
    sortBy: sortBy ?? null,
    sortOrder: sortOrder ?? null,
  };
}

export function prefetchJobsPaginatedPage(
  params: JobsPaginatedQueryParams
): Promise<void> {
  return queryClient.prefetchQuery(
    getJobsPaginatedQueryKey(params),
    () => fetchJobsPaginatedPage(params),
    { ...prefetchQueryOptions, staleTime: 1000 * 60 }
  );
}

export function prefetchJobsStatusCounts(clusterName: string): Promise<void> {
  return queryClient.prefetchQuery(
    [QueryServerKeys.JOBS.GET_STATUS_COUNTS, clusterName],
    () => getStatusCounts({ clusterName }),
    { ...prefetchQueryOptions, staleTime: 1000 * 60 }
  );
}

export function prefetchSchemasAndTables(): Promise<void> {
  return queryClient.prefetchQuery(
    [QueryServerKeys.LISTS.GET_SCHEMAS_AND_TABLES],
    async () => {
      const res = await getSchemasAndTables();
      return res.data;
    },
    { ...prefetchQueryOptions, staleTime: 1000 * 60 }
  );
}

/** Prefetch jobs page 1, status counts, and schema/table lists (matches initial Jobs table state). */
export function prefetchDefaultJobsFirstPage({
  clusterName,
  sortBy,
  sortOrder,
}: {
  clusterName: string;
  sortBy?: string | null;
  sortOrder?: string | null;
}): Promise<unknown[]> {
  const jobsParams = buildDefaultJobsFirstPageParams({
    clusterName,
    sortBy,
    sortOrder,
  });

  return Promise.all([
    prefetchJobsStatusCounts(clusterName),
    prefetchJobsPaginatedPage(jobsParams),
    prefetchSchemasAndTables(),
  ]);
}
